import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * @typedef {Object} FileState
 * @property {string} key
 * @property {File} file
 * @property {PDFDocumentProxy} [pdfDocument]
 * @property {string} [password]
 * @property {string} [hash]
 * @property {Error} [error]
 */

/**
 * @typedef {Object} PdfFileState
 * @property {FileState} file
 * @property {Promise<PDFPageProxy>[]} pages
 * @property {number[]} rotations
 * @property {number[]} deletions
 */

/**
 * @typedef {Object} usePdfFilesReturn
 * @property {PdfFileState[]} pdfFiles
 * @property {number} totalPages
 * @property {function(fileIndex: number, pages: number[]): void} deletePages
 * @property {function(fileIndex: number, pages: number[], angle: number): void} rotatePages
 * @property {function(angle: number): void} rotateAllPages
 * @property {function(selection: {[number]: number[]}, flag: boolean?): void} selectPages
 * @property {function(fileIndex: number): void} selectFile
 */

/**
 * @param {FileState[]} files
 * @param {function} setFiles
 * @return {usePdfFilesReturn}
 */
const usePdfFiles = (files, setFiles) => {
  const [state, setState] = useState(() => ({
    pdfFiles: [],
    filesToDelete: [],
  }));

  const totalPages = useMemo(() => {
    return state.pdfFiles.reduce((totalPages, pdfFile) => {
      return totalPages + pdfFile.file.pdfDocument.numPages;
    }, 0);
  }, [state.pdfFiles]);

  const deletePages = useCallback((fileIndex, pages) => {
    setState((state) => {
      const pdfFile = state.pdfFiles[fileIndex];

      if (!pdfFile) {
        return state;
      }

      const numPages = pdfFile.file.pdfDocument.numPages;

      pages = pages.filter((pageNumber) => {
        return pageNumber >= 1 && pageNumber <= numPages;
      });

      if (!pages.length) {
        return state;
      }

      pdfFile.deletions = [...new Set(pdfFile.deletions.concat(pages))];

      if (pdfFile.deletions.length >= numPages) {
        return {
          ...state,
          filesToDelete: [fileIndex],
        };
      }

      pdfFile.rotations = pdfFile.rotations.map((rotation, i) => {
        return pages.includes(i + 1) ? 0 : rotation;
      });

      pdfFile.selections = pdfFile.selections.filter((pageNumber) => {
        return !pages.includes(pageNumber);
      });

      state.pdfFiles[fileIndex] = { ...pdfFile };

      return {
        ...state,
        pdfFiles: state.pdfFiles.slice(),
      };
    });
  }, []);

  const rotatePages = useCallback((fileIndex, pages, angle) => {
    setState((state) => {
      const pdfFile = state.pdfFiles[fileIndex];

      if (!pdfFile) {
        return state;
      }

      const numPages = pdfFile.file.pdfDocument.numPages;

      pages = pages.filter((pageNumber) => {
        return pageNumber >= 1 && pageNumber <= numPages && !pdfFile.deletions.includes(pageNumber);
      });

      if (!pages.length) {
        return state;
      }

      const rotations = pdfFile.rotations;

      pages.forEach((pageNumber) => {
        const pageIndex = pageNumber - 1;

        rotations[pageIndex] = (rotations[pageIndex] + angle + 360) % 360;
      });

      pdfFile.rotations = rotations.slice();
      state.pdfFiles[fileIndex] = { ...pdfFile };

      return {
        ...state,
        pdfFiles: state.pdfFiles.slice(),
      };
    });
  }, []);

  const rotateAllPages = useCallback((angle) => {
    setState((state) => ({
      ...state,
      pdfFiles: state.pdfFiles.map((pdfFile) => ({
        ...pdfFile,
        rotations: pdfFile.rotations.map((rotation) => {
          return (rotation + angle + 360) % 360;
        }),
      })),
    }));
  }, []);

  const selectPages = useCallback((selection, flag = null) => {
    setState((state) => {
      let modified = false;

      Object.entries(selection).forEach(([fileIndex, pages]) => {
        const pdfFile = state.pdfFiles[fileIndex];

        if (!pdfFile) {
          return;
        }

        const numPages = pdfFile.file.pdfDocument.numPages;

        pages = pages.filter((pageNumber) => {
          return pageNumber >= 1 && pageNumber <= numPages && !pdfFile.deletions.includes(pageNumber);
        });

        if (!pages.length && flag !== true) {
          return;
        }

        if (flag === true) {
          pdfFile.selections = pages.sort((a, b) => a - b);
        } else {
          const selections = pdfFile.selections;

          pages.forEach((pageNumber) => {
            const index = selections.indexOf(pageNumber);

            if (flag === false) {
              if (index !== -1) {
                selections.splice(index, 1);
              }

              return;
            }

            if (index === -1) {
              selections.push(pageNumber);
            } else {
              selections.splice(index, 1);
            }
          });

          pdfFile.selections = selections.sort((a, b) => a - b).slice();
        }

        state.pdfFiles[fileIndex] = { ...pdfFile };
        modified = true;
      });

      const pdfFiles = modified ? state.pdfFiles.slice() : state.pdfFiles;

      return {
        ...state,
        pdfFiles,
      };
    });
  }, []);

  const selectFile = useCallback((fileIndex) => {
    setState((state) => {
      const pdfFile = state.pdfFiles[fileIndex];

      if (!pdfFile) {
        return state.pdfFiles;
      }

      if (pdfFile.selections.length) {
        pdfFile.selections = [];
      } else {
        pdfFile.selections = [...new Array(pdfFile.file.pdfDocument.numPages)]
          .filter((_, i) => !pdfFile.deletions.includes(i + 1))
          .map((_, i) => i + 1);
      }

      state.pdfFiles[fileIndex] = { ...pdfFile };

      return {
        ...state,
        pdfFiles: state.pdfFiles.slice(),
      };
    });
  }, []);

  useEffect(() => {
    if (state.filesToDelete.length) {
      setFiles((files) => {
        return files.filter((file, i) => !state.filesToDelete.includes(i));
      });
    }
  }, [state.filesToDelete, setFiles]);

  useEffect(() => {
    setState((state) => ({
      ...state,
      pdfFiles: files.map((file) => {
        const pdfFile = state.pdfFiles.find((pdfFile) => pdfFile.file.key === file.key);

        // new file
        if (!pdfFile) {
          const numPages = file.pdfDocument.numPages;

          return {
            file,
            pages: [...new Array(numPages)].map((_, i) => {
              return file.pdfDocument.getPage(i + 1);
            }),
            deletions: [],
            rotations: new Array(numPages).fill(0),
            selections: [],
          };
        }

        // updated file
        if (pdfFile.file !== file) {
          return {
            ...pdfFile,
            file,
          };
        }

        // no changes
        return pdfFile;
      }),
    }));
  }, [files]);

  return {
    pdfFiles: state.pdfFiles,
    totalPages,
    deletePages,
    rotatePages,
    rotateAllPages,
    selectPages,
    selectFile,
  };
};

export default usePdfFiles;
